import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, Legend, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowUpFromLine, ArrowDownToLine, Scale, ReceiptIndianRupee, ShieldAlert } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { FxExposureCard } from "@/components/FxExposureCard";
import { FundedExposureLine } from "@/components/FinancingSummary";
import { PinnedInsights } from "@/components/PinnedInsights";
import { useAgeingBasis, AgeingBasisToggle, BASIS_LABEL } from "@/hooks/useAgeingBasis";
import { API } from "@/context/AuthContext";

export const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

export const inrShort = (v) => {
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return inr(v);
};

const KPI = ({ label, value, sub, icon: Icon, tone, testId, onClick }) => (
  <Card
    className={`border-slate-200 shadow-sm fade-in-up ${onClick ? "cursor-pointer transition-all duration-200 hover:shadow-md hover:border-sky-300 hover:-translate-y-0.5" : ""}`}
    data-testid={testId}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">{label}</p>
          <p className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900">{value}</p>
          {sub && <p className={`text-xs mt-1.5 font-medium ${tone}`}>{sub}</p>}
          {onClick && <p className="text-[11px] mt-1 font-medium text-sky-600">View report →</p>}
        </div>
        <div className="h-9 w-9 rounded-md bg-sky-50 flex items-center justify-center">
          <Icon className="h-4.5 w-4.5 text-sky-700" style={{ height: 18, width: 18 }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [projects, setProjects] = useState([]);
  const [basis, setBasis] = useAgeingBasis();
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = { ...(projects.length ? { project: projects.join(",") } : {}), basis };
    axios.get(`${API}/dashboard/summary`, { params }).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/reports/ageing-trend`, { params: { months: 6, basis } }).then((r) => setTrend(r.data.points)).catch(() => {});
  }, [projects, basis]);

  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, [load]);

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading dashboard...</div>;

  const chartData = data.buckets.map((b) => ({
    bucket: b,
    "Payables (AP)": data.ap_summary[b],
    "Receivables (AR)": data.ar_summary[b],
  }));
  const withProject = (path) => (projects.length ? `${path}?project=${encodeURIComponent(projects.join(","))}` : path);

  return (
    <div className="space-y-6" data-testid="main-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Financial Overview</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live position extracted from SAP S/4HANA Public Cloud
            {projects.length > 0 && <span className="font-semibold text-sky-700"> · {projects.length === 1 ? `Project ${projects[0]}` : `${projects.length} projects`}</span>}
          </p>
        </div>
        <div className="flex flex-col items-start gap-1">
          <SearchableSelect
            multiple
            options={(data.projects || []).map((p) => ({ value: p.code, label: p.name || p.code, sublabel: p.code }))}
            value={projects}
            allowPattern
            onChange={setProjects}
            allLabel="All Projects"
            placeholder="All Projects"
            width="w-64"
            testId="dashboard-project-select"
          />
          <span className="text-[11px] text-slate-400 pl-1">Filter AP &amp; AR balances by projects — or type a WBS pattern like 2506* / *FUR*</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-total-ap" label="Total Payables (AP · Credit)" value={inrShort(data.ap_total)} icon={ArrowUpFromLine}
             sub={`${inrShort(data.ap_overdue)} overdue · ${data.ap_count} invoices`} tone="text-red-600" onClick={() => navigate(withProject("/ap-ageing"))} />
        <KPI testId="kpi-total-ar" label="Total Receivables (AR · Debit)" value={inrShort(data.ar_total)} icon={ArrowDownToLine}
             sub={`${inrShort(data.ar_overdue)} overdue · ${data.ar_count} invoices`} tone="text-amber-600" onClick={() => navigate(withProject("/ar-ageing"))} />
        <KPI testId="kpi-net-position" label="Net Position (AR − AP)" value={inrShort(data.net_position)} icon={Scale}
             sub={data.net_position >= 0 ? "Net receivable" : "Net payable"} tone={data.net_position >= 0 ? "text-emerald-600" : "text-red-600"} />
        <KPI testId="kpi-itc" label="ITC Available (GSTR-2)" value={inrShort(data.itc_available)} icon={ReceiptIndianRupee}
             sub={`${data.gstr2_count} purchase invoices`} tone="text-sky-700" onClick={() => navigate("/gstr2")} />
      </div>

      <FundedExposureLine financing={data.financing} />
      <FxExposureCard exposure={data.fx_exposure} project={projects} />

      {data.msme && data.msme.count > 0 && (
        <Card
          className="border-amber-200 shadow-sm fade-in-up cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
          data-testid="msme-widget"
          onClick={() => navigate(withProject("/ap-ageing"))}
          role="button"
        >
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div className="flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <ShieldAlert className="text-amber-600" style={{ height: 22, width: 22 }} />
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-1">MSME Suppliers — Outstanding</p>
                  <p className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900" data-testid="msme-total">{inrShort(data.msme.total)}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{data.msme.count} open invoices to registered MSME / Udyam vendors</p>
                </div>
              </div>
              <div className={`rounded-lg px-5 py-3 border ${data.msme.breach > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${data.msme.breach > 0 ? "text-red-600" : "text-emerald-700"}`}>Beyond 45-day MSME limit</p>
                <p className={`text-2xl font-black tracking-tighter mt-0.5 ${data.msme.breach > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="msme-breach">{inrShort(data.msme.breach)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {data.msme.breach_count} invoices
                  {data.msme.interest > 0 && <> · est. interest <span className="font-semibold text-red-600">~{inrShort(data.msme.interest)}</span></>}
                </p>
              </div>
            </div>
            <p className="text-[11px] mt-3 font-medium text-sky-600">View MSME payables in AP Ageing →</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg font-semibold text-slate-800">Ageing Distribution — AP vs AR <span className="text-xs font-normal text-slate-500">· by {BASIS_LABEL[basis]}</span></CardTitle>
          <AgeingBasisToggle basis={basis} onChange={setBasis} testPrefix="dashboard" />
        </CardHeader>
        <CardContent>
          <div className="h-80" data-testid="dashboard-ageing-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Payables (AP)" fill="#0369A1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Receivables (AR)" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800">Month-over-Month Trend — Outstanding & Overdue</CardTitle>
          <p className="text-xs text-slate-500 font-normal">Position recalculated as of each month-end · last 6 months</p>
        </CardHeader>
        <CardContent>
          <div className="h-72" data-testid="dashboard-trend-chart">
            {trend ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="ap_total" name="AP Outstanding" stroke="#0369A1" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="ap_overdue" name="AP Overdue" stroke="#0369A1" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="ar_total" name="AR Outstanding" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="ar_overdue" name="AR Overdue" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="animate-pulse text-slate-400 text-sm">Loading trend...</div>
            )}
          </div>
        </CardContent>
      </Card>

      <PinnedInsights />
    </div>
  );
}
